import { useEffect, useRef } from 'react';
import {
    decideSessionAction,
    latestActivityAt,
    SESSION_ACTIVITY_CHECK_INTERVAL_MS,
    SESSION_ACTIVITY_EVENTS,
    SESSION_LAST_ACTIVITY_STORAGE_KEY,
} from '../domain/sessionActivity';

interface SessionActivityOptions {
    /** Only a logged-in session is watched. */
    enabled: boolean;
    idleTimeoutMinutes: number;
    /** The user has been idle for the whole timeout. */
    onIdle: () => void;
    /** The user has been active since the last heartbeat; keep the server session open. */
    onHeartbeat: () => Promise<void>;
}

function readStoredActivity(): string | null {
    try {
        return localStorage.getItem(SESSION_LAST_ACTIVITY_STORAGE_KEY);
    } catch {
        return null;
    }
}

function storeActivity(activityAt: number): void {
    try {
        localStorage.setItem(SESSION_LAST_ACTIVITY_STORAGE_KEY, String(activityAt));
    } catch {
        // Private mode or blocked storage: other tabs just cannot see this tab's activity.
    }
}

/**
 * Keeps a staff session open while the user works and ends it once they stop.
 *
 * Activity events only stamp a ref, so a moving mouse costs nothing; the comparison with the
 * idle timeout, the heartbeat and the cross-tab stamp all happen on a slow timer, and again
 * whenever the tab becomes visible (a laptop waking from sleep must not wait for the timer).
 */
export function useSessionActivity({
    enabled,
    idleTimeoutMinutes,
    onIdle,
    onHeartbeat,
}: SessionActivityOptions): void {
    const lastActivityAtRef = useRef(Date.now());
    const lastHeartbeatAtRef = useRef(0);
    const heartbeatInFlightRef = useRef(false);
    const idleTimeoutRef = useRef(idleTimeoutMinutes);
    const onIdleRef = useRef(onIdle);
    const onHeartbeatRef = useRef(onHeartbeat);

    idleTimeoutRef.current = idleTimeoutMinutes;
    onIdleRef.current = onIdle;
    onHeartbeatRef.current = onHeartbeat;

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        // Opening or reloading the page is itself activity.
        lastActivityAtRef.current = Date.now();
        lastHeartbeatAtRef.current = 0;
        storeActivity(lastActivityAtRef.current);

        const recordActivity = () => {
            lastActivityAtRef.current = Date.now();
        };

        const sendHeartbeat = async (sentAt: number) => {
            heartbeatInFlightRef.current = true;
            try {
                await onHeartbeatRef.current();
                lastHeartbeatAtRef.current = sentAt;
            } finally {
                heartbeatInFlightRef.current = false;
            }
        };

        const checkSession = () => {
            const now = Date.now();
            const storedActivity = readStoredActivity();
            const lastActivityAt = latestActivityAt(lastActivityAtRef.current, storedActivity);

            if (lastActivityAt > Number(storedActivity)) {
                storeActivity(lastActivityAt);
            }
            lastActivityAtRef.current = lastActivityAt;

            const action = decideSessionAction({
                now,
                lastActivityAt,
                lastHeartbeatAt: lastHeartbeatAtRef.current,
                idleTimeoutMinutes: idleTimeoutRef.current,
            });

            if (action === 'logout') {
                onIdleRef.current();
            } else if (action === 'heartbeat' && !heartbeatInFlightRef.current) {
                void sendHeartbeat(now);
            }
        };

        const checkWhenVisible = () => {
            if (document.visibilityState === 'visible') {
                checkSession();
            }
        };

        SESSION_ACTIVITY_EVENTS.forEach(eventName =>
            window.addEventListener(eventName, recordActivity, { passive: true }),
        );
        document.addEventListener('visibilitychange', checkWhenVisible);
        const intervalId = window.setInterval(checkSession, SESSION_ACTIVITY_CHECK_INTERVAL_MS);
        checkSession();

        return () => {
            SESSION_ACTIVITY_EVENTS.forEach(eventName =>
                window.removeEventListener(eventName, recordActivity),
            );
            document.removeEventListener('visibilitychange', checkWhenVisible);
            window.clearInterval(intervalId);
        };
    }, [enabled]);
}
